import { URLS } from '@/constants';
import { PaginatedResponse } from '@/models/api';
import {
  Connection,
  ConnectionCompleteParamsMap,
  ConnectionInstallResponseMap,
  ExternalIntegrationCustomer,
  ExternalIntegrationItem,
  GetConnectionsParams,
  PatchConnectionArgs,
  PostBioTrackUserCredentialsArgs,
  PostResourceMappingsSearchArgs,
  PutResourceMappingsArgs,
  ResourceMappings,
} from '@/models/connections';
import { buildPath } from '@/utils';

import { integrationsSession } from '../session';

export const getConnectionInstallToken = async <TType extends keyof ConnectionInstallResponseMap>({
  connectionType,
  params,
}: {
  connectionType: TType;
  params?: Record<string, unknown>;
}) => {
  // currently only `marketplace-quikbooks` is supported, but more connection types will be added over time
  const path = buildPath(URLS.CONNECTIONS.INSTALL, { connectionType });
  const response = await integrationsSession.get<ConnectionInstallResponseMap[TType]>(path, { params });

  return response.data;
};

export const getConnectionComplete = async <TType extends keyof ConnectionCompleteParamsMap>({
  connectionType,
  params,
}: {
  connectionType: TType;
  params?: ConnectionCompleteParamsMap[TType];
}) => {
  const path = buildPath(URLS.CONNECTIONS.COMPLETE, { connectionType });
  const response = await integrationsSession.get<ConnectionCompleteParamsMap[TType]>(path, { params });

  return response.data;
};

export const getConnections = async (params?: GetConnectionsParams) => {
  const response = await integrationsSession.get<PaginatedResponse<Connection>>(URLS.CONNECTIONS.LIST, { params });

  return response.data;
};

export const postResourceMappingsSearch = async ({ connectionId, body }: PostResourceMappingsSearchArgs) => {
  const response = await integrationsSession.post<ResourceMappings[]>(
    `${URLS.CONNECTIONS.RESOURCE_MAPPINGS_SEARCH}?connection_id=${connectionId}`,
    body,
  );

  return response.data;
};

export const patchConnection = async ({ connectionId, body }: PatchConnectionArgs) => {
  const response = await integrationsSession.patch<Connection>(
    `${URLS.CONNECTIONS.QUICKBOOKS_SETTINGS}?connection_id=${connectionId}`,
    body,
  );
  return response.data;
};

export const putResourceMappings = async ({ connectionId, body }: PutResourceMappingsArgs) => {
  const response = await integrationsSession.put(
    `${URLS.CONNECTIONS.RESOURCE_MAPPINGS}?connection_id=${connectionId}`,
    body,
  );

  return response.data;
};

export const getExternalIntegrationCustomers = async (params: { connection_id: string; name: string }) => {
  const response = await integrationsSession.get<PaginatedResponse<ExternalIntegrationCustomer>>(
    URLS.CONNECTIONS.QUICKBOOKS_CUSTOMERS,
    { params },
  );

  return response.data;
};

export const getExternalIntegrationItems = async (params: { connection_id: string; name: string }) => {
  const response = await integrationsSession.get<PaginatedResponse<ExternalIntegrationItem>>(
    URLS.CONNECTIONS.QUICKBOOKS_ITEMS,
    { params },
  );

  return response.data;
};

export const postBioTrackUserCredentials = async ({ connectionId, body }: PostBioTrackUserCredentialsArgs) => {
  const response = await integrationsSession.post(
    `${URLS.CONNECTIONS.USER_CREDENTIALS_BIOTRACK}?connection_id=${connectionId}`,
    body,
  );

  return response.data;
};

export const deleteBioTrackUserCredentials = async ({ connectionId }: { connectionId: string }) => {
  const response = await integrationsSession.delete(
    `${URLS.CONNECTIONS.USER_CREDENTIALS_BIOTRACK}?connection_id=${connectionId}`,
  );

  return response.data;
};
